/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/ban-types */
import {
  ResultList,
  ResultListState,
  SearchEngine,
  buildResultList,
  loadAdvancedSearchQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  buildResultsPerPage,
} from '@coveo/headless';
import { isEditorActive, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { getStateFromZipcode } from 'lib/utils/coveoHeadless';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';

const FIELDS_TO_INCLUDE: string[] = [
  'trialminage',
  'trialmaxage',
  'trialphase',
  'trialcompletiondate',
  'trialenrollmenttype',
  'trialstatus',
  'trialenrollment',
  'trialorgstudyid',
  'isvirtual',
  'trialenrollingstatus',
  'trialname',
  'isfeaturedtrial',
  'featuredeprurl',
  'distance',
  'locationcountry',
  'locationcity',
  'locationsitename',
  'locationaddress',
  'locationsitecoreid',
  'locationstatus',
  'locationstate',
  'locationzip',
  'triallatitude',
  'triallongitude',
];

const useZipcodeSearch = (
  engine: SearchEngine<{}>,
  zipCode: string,
  trialOrgStudyId: string,
  distance: number
): { state?: ResultListState } => {
  const dispatch = useDispatch();
  const [state, setState] = useState<ResultListState>();
  const resultListRef = useRef<ResultList | null>(null);
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;

  const isProfile = Object.keys(userProfile.profileData).length !== 0;
  const [locationStateByZipCode, setLocationStateByZipCode] = useState<{
    latitude?: number;
    longitude?: number;
  }>();
  const { sitecoreContext } = useSitecoreContext();

  useEffect(() => {
    if (!zipCode) return;
    const fetchLocationData = async () => {
      try {
        const stateFromZipCode: any = await getStateFromZipcode(zipCode);
        if (stateFromZipCode) {
          setLocationStateByZipCode({
            latitude: stateFromZipCode.latitude,
            longitude: stateFromZipCode.longitude,
          });
        }
      } catch (_error) {
        setLocationStateByZipCode({
          latitude: undefined,
          longitude: undefined,
        });
      }
    };

    fetchLocationData();
  }, [isProfile, profileDetails?.zipCode, zipCode]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') return;
    const searchQueryActionCreators = loadAdvancedSearchQueryActions(engine);
    const trialLocationGovSource =
      process.env.NEXT_PUBLIC_COVEO_CTGOV_LOCATION_SOURCE || 'Trial Location Gov Dev';
    const trialLocationSource =
      process.env.NEXT_PUBLIC_COVEO_CTMS_LOCATION_SOURCE || 'Trial Locations Dev';
    const advancedSearchQueriesOptions: { aq: string } = {
      aq: `@source=("${trialLocationSource}" OR "${trialLocationGovSource}") @distance<=${distance} @trialorgstudyid=(${trialOrgStudyId}) 
      $qf(function:'dist(@triallatitude, @triallongitude, ${locationStateByZipCode?.latitude}, 
      ${locationStateByZipCode?.longitude})*0.000621371', 
      fieldName: 'distance')$sort(criteria: 'fieldascending', field: '@distance')`,
    };

    const searchQueryAction = searchQueryActionCreators.updateAdvancedSearchQueries(
      advancedSearchQueriesOptions
    );

    engine.dispatch(searchQueryAction);
    const { executeSearch } = loadSearchActions(engine);
    const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
    engine.dispatch(executeSearch(logInterfaceLoad()));

    const resultList: ResultList = buildResultList(engine, {
      options: {
        fieldsToInclude: FIELDS_TO_INCLUDE,
      },
    });

    resultListRef.current = resultList;
    setState(resultList.state);

    resultList.subscribe(() => {
      setState(resultList.state);
    });

    return () => {
      resultList.subscribe(() => {});
    };
  }, [
    dispatch,
    engine,
    zipCode,
    locationStateByZipCode?.latitude,
    locationStateByZipCode?.longitude,
  ]);

  useEffect(() => {
    buildResultsPerPage(engine, {
      initialState: { numberOfResults: 100000 },
    });
  }, []);

  return { state };
};

export default useZipcodeSearch;
